"use client";

import Image from "next/image";
import { useTranslation } from "react-i18next";
import Link from "@/app/components/LocalizedLink";
import { useCurrency } from "@/app/components/providers/CurrencyProvider";
import { formatPrice, getPriceForPlan } from "@/app/utils/currency-config";
import { AnimatedIcon } from "../AnimatedIcon";
import { ARROW_ICON } from "../animatedIcons";
import AnimatedCounter from "./AnimatedCounter";
import { CheckMark } from "./BusinessMarks";
import Reveal from "./Reveal";
import SectionHeading from "./SectionHeading";
import TiltCard from "./TiltCard";

const CAMPAIGN_CREATIVES = [
  "/images/creatives/group1-square.png",
  "/images/6.png",
  "/images/creatives/group3-square.png",
  "/images/4.png",
];

function asList(value: unknown): string[] {
  return Array.isArray(value) ? (value as string[]) : [];
}

function PlanList({
  items,
  tone,
}: {
  items: string[];
  tone: "light" | "blue";
}) {
  if (items.length === 0) return null;
  return (
    <ul className="grid gap-2.5">
      {items.map(item => (
        <li
          key={item}
          className="glass-inset flex items-center gap-3 rounded-2xl py-2.5 pl-3 pr-5 text-[15px] leading-snug text-signal-ink"
        >
          <span
            className={tone === "blue" ? "glass-check-paid" : "glass-check"}
          >
            <CheckMark className="h-3.5 w-3.5" />
          </span>
          {item}
        </li>
      ))}
    </ul>
  );
}

/**
 * The price of a plan, as a medallion rather than a line of type. It sits in
 * a tilting card, so it is flat glass; the card's tint shows through it.
 */
function PriceMedallion({
  prefix,
  price,
  period,
}: {
  prefix?: string;
  price: string;
  period: string;
}) {
  return (
    <p className="glass-flat flex h-[148px] w-[148px] shrink-0 flex-col items-center justify-center gap-0.5 rounded-full px-3 text-center">
      {prefix && (
        <span className="text-[12px] text-signal-muted">{prefix}</span>
      )}
      <span className="font-signal-mono text-[clamp(1.6rem,3vw,2.1rem)] font-semibold leading-none tabular-nums text-signal-ink">
        {price}
      </span>
      <span className="text-[12px] leading-tight text-signal-muted">
        {period}
      </span>
    </p>
  );
}

export default function ValueBento() {
  const { t } = useTranslation();
  const { currency } = useCurrency();

  const infrastructureItems = asList(
    t("landing.valueTriptych.infrastructure.items", { returnObjects: true })
  );
  const reachItems = asList(
    t("landing.valueTriptych.reach.items", { returnObjects: true })
  );
  const statNumberClass =
    "font-signal-mono text-[clamp(1.25rem,5.5vw,1.6rem)] sm:text-[clamp(1.6rem,3vw,2.25rem)] font-bold leading-none tabular-nums tracking-[-0.03em] text-signal-accent";

  const resultStats = [
    {
      key: "applications",
      label: t("landing.valueTriptych.results.stats.applications.label"),
      value: (
        <AnimatedCounter end={10} suffix="x" className={statNumberClass} />
      ),
    },
    {
      key: "cost",
      label: t("landing.valueTriptych.results.stats.cost.label"),
      value: (
        <AnimatedCounter
          end={70}
          prefix="-"
          suffix="%"
          className={statNumberClass}
        />
      ),
    },
    {
      key: "time",
      label: t("landing.valueTriptych.results.stats.time.label"),
      value: (
        <>
          <AnimatedCounter end={14} className={statNumberClass} />
          <span className="text-[11px] font-medium text-signal-muted sm:text-[13px]">
            {t("landing.valueTriptych.results.stats.time.unit")}
          </span>
        </>
      ),
    },
  ];

  return (
    <section
      aria-labelledby="value-title"
      className="relative overflow-hidden py-20 lg:py-28"
    >
      <div className="relative mx-auto w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col items-center">
          <SectionHeading
            id="value-title"
            title={t("landing.valueTriptych.sectionTitle")}
            lead={t("landing.valueTriptych.infrastructure.description")}
          />
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-8 lg:mt-16 lg:grid-cols-2 lg:gap-10">
          {/* Plan: free infrastructure */}
          <Reveal>
            <TiltCard
              restX={2}
              restY={4}
              strength={4}
              lift={14}
              className="glass-flat flex h-full flex-col rounded-glass p-7 sm:p-9"
            >
              <div className="flex flex-wrap items-center justify-between gap-6">
                <h3 className="glass-inset rounded-full px-5 py-2 text-[15px] font-semibold text-signal-ink">
                  {t("landing.valueTriptych.infrastructure.title")}
                </h3>
                <PriceMedallion
                  price={formatPrice(0, currency)}
                  period={t("landing.valueTriptych.infrastructure.period")}
                />
              </div>
              <div className="mt-8">
                <PlanList items={infrastructureItems} tone="light" />
              </div>
              <div className="mt-auto pt-9">
                <a
                  href="#hero-title"
                  className="btn-glass-ghost group h-12 pl-7 pr-4 text-[15px]"
                >
                  {t("landing.valueTriptych.infrastructure.cta")}
                  <span
                    aria-hidden="true"
                    className="glass-arrow text-[13px] transition-transform duration-300 group-hover:translate-x-0.5"
                  >
                    <AnimatedIcon
                      icon={ARROW_ICON}
                      className="h-[1.15em] w-[1.15em]"
                    />
                  </span>
                </a>
              </div>
            </TiltCard>
          </Reveal>

          {/* Plan: paid social campaigns, raised out of the page in the
              blue-tinted glass of the featured plan */}
          <Reveal delay={100}>
            <TiltCard
              restX={2}
              restY={-4}
              strength={4}
              lift={18}
              className="glass-flat tint-blue flex h-full flex-col rounded-glass p-7 sm:p-9"
            >
              <div className="flex flex-wrap items-center justify-between gap-6">
                <h3 className="btn-glass-primary px-5 py-2 text-[15px]">
                  {t("landing.valueTriptych.reach.title")}
                </h3>
                <PriceMedallion
                  prefix={t("landing.valueTriptych.reach.pricePrefix")}
                  price={formatPrice(
                    getPriceForPlan(currency, "lite"),
                    currency
                  )}
                  period={t("landing.valueTriptych.reach.period")}
                />
              </div>

              <div
                aria-hidden="true"
                className="mt-7 flex -space-x-5 [transform:translateZ(30px)]"
              >
                {CAMPAIGN_CREATIVES.map((src, index) => (
                  <span
                    key={src}
                    className="relative h-16 w-16 overflow-hidden rounded-full bg-signal-tint shadow-orbitAvatar transition-transform duration-500 hover:-translate-y-1.5"
                    style={{ zIndex: CAMPAIGN_CREATIVES.length - index }}
                  >
                    <Image
                      src={src}
                      alt=""
                      fill
                      sizes="64px"
                      className="object-cover"
                    />
                  </span>
                ))}
              </div>

              <p className="mt-7 text-pretty text-[15px] leading-relaxed text-signal-muted">
                {t("landing.valueTriptych.reach.description")}
              </p>
              <div className="mt-6">
                <PlanList items={reachItems} tone="blue" />
              </div>
              <Link
                href="/home/pricing"
                className="btn-glass-dark group mt-auto h-12 self-start pl-7 pr-4 text-[15px] lg:mt-9"
              >
                {t("landing.valueTriptych.reach.cta")}
                {/* On a filled button the material draws the arrow as a ring */}
                <span
                  aria-hidden="true"
                  className="glass-arrow transition-transform duration-300 group-hover:translate-x-0.5"
                >
                  <AnimatedIcon
                    icon={ARROW_ICON}
                    className="h-[1.15em] w-[1.15em]"
                  />
                </span>
              </Link>
            </TiltCard>
          </Reveal>
        </div>

        {/* Results: three gauges resting on the section floor. The dome only
            reads as a pill on wide screens; narrower, its arch would clip the
            copy, so it shares the bento cards' pod corners there. */}
        <Reveal
          delay={150}
          className="glass mt-8 grid grid-cols-1 items-center gap-8 rounded-glass px-5 py-8 sm:gap-10 sm:px-10 sm:py-9 lg:grid-cols-12 lg:rounded-full lg:px-12 lg:py-7"
        >
          <div className="lg:col-span-4">
            <h3 className="text-[15px] font-semibold text-signal-deep">
              {t("landing.valueTriptych.results.title")}
            </h3>
            <p className="mt-3 max-w-[40ch] text-pretty text-[15px] leading-relaxed text-signal-muted">
              {t("landing.valueTriptych.results.description")}
            </p>
          </div>
          <dl className="grid grid-cols-3 justify-items-center gap-3 sm:gap-7 lg:col-span-8">
            {resultStats.map((stat, index) => (
              <div
                key={stat.key}
                className="flex w-full max-w-[150px] flex-col items-center text-center"
              >
                {/* Floating coins animate every frame, so they are flat glass */}
                <dd
                  className="glass-flat flex aspect-square w-full max-w-[124px] items-center justify-center gap-1 rounded-full animate-orbit-float motion-reduce:animate-none"
                  style={{ animationDelay: `${index * 800}ms` }}
                >
                  {stat.value}
                </dd>
                <dt className="mt-3 text-[12px] leading-snug text-signal-muted sm:text-[13px]">
                  {stat.label}
                </dt>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
    </section>
  );
}
